import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../../hooks/useNotifications';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';

interface ClienteFormData {
  nome: string;
  email: string;
  telefone: string;
  dataNascimento: string;
  nacionalidade: string;
  passaporteNumero: string;
  passaporteValidade: string;
  endereco: {
    logradouro: string;
    numero: string;
    complemento: string;
    bairro: string;
    cidade: string;
    cep: string;
    pais: string;
  };
  status: 'lead' | 'negociacao' | 'cliente_ativo' | 'inativo';
  origem: string;
  observacoes: string;
  responsavelId: string;
}

const emptyFormData = (): ClienteFormData => ({
  nome: '',
  email: '',
  telefone: '',
  dataNascimento: '',
  nacionalidade: 'brasileira',
  passaporteNumero: '',
  passaporteValidade: '',
  endereco: { logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', cep: '', pais: 'Portugal' },
  status: 'lead',
  origem: '',
  observacoes: '',
  responsavelId: ''
});

interface UserOption {
  _id: string;
  name: string;
}

const ClienteForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const isEditing = Boolean(id);
  const notifications = useNotifications();

  const [formData, setFormData] = useState<ClienteFormData>(emptyFormData());
  const [users, setUsers] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchCliente = useCallback(async () => {
    if (!id) return;
    try {
      const response = await axios.get(`/clientes/${id}`);
      const cliente = response.data.cliente;
      setFormData({
        nome: cliente.nome || '',
        email: cliente.email || '',
        telefone: cliente.telefone || '',
        dataNascimento: cliente.dataNascimento ? cliente.dataNascimento.slice(0, 10) : '',
        nacionalidade: cliente.nacionalidade || 'brasileira',
        passaporteNumero: cliente.passaporteNumero || '',
        passaporteValidade: cliente.passaporteValidade ? cliente.passaporteValidade.slice(0, 10) : '',
        endereco: {
          logradouro: cliente.endereco?.logradouro || '',
          numero: cliente.endereco?.numero || '',
          complemento: cliente.endereco?.complemento || '',
          bairro: cliente.endereco?.bairro || '',
          cidade: cliente.endereco?.cidade || '',
          cep: cliente.endereco?.cep || '',
          pais: cliente.endereco?.pais || 'Portugal'
        },
        status: cliente.status || 'lead',
        origem: cliente.origem || '',
        observacoes: cliente.observacoes || '',
        responsavelId: cliente.responsavelId?._id || ''
      });
    } catch (error) {
      console.error('Erro ao carregar cliente:', error);
    }
  }, [id]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get('/users?limit=100');
      setUsers(response.data.users);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (isEditing && id) {
      fetchCliente();
    }
  }, [isEditing, id, fetchCliente]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (formData.nome.trim().length < 1) {
      notifications.warning.validation('Informe o nome do cliente.');
      setLoading(false);
      return;
    }

    const payload = {
      ...formData,
      responsavelId: formData.responsavelId || null
    };

    try {
      if (isEditing) {
        await axios.put(`/clientes/${id}`, payload);
        notifications.success.clienteUpdated();
      } else {
        await axios.post('/clientes', payload);
        notifications.success.clienteCreated();
      }
      setTimeout(() => navigate('/admin/crm/clientes'), 1000);
    } catch (error: any) {
      console.error('Erro ao salvar cliente:', error);
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map((err: any) => err.msg).join(', ');
        notifications.error.clienteCreate(errorMessages);
      } else {
        notifications.error.clienteCreate(error.response?.data?.message || 'Erro ao salvar cliente');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="mb-4 px-4 sm:px-0">
          <Breadcrumbs />
        </div>
        <div className="mb-8 px-4 sm:px-0">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin/crm/clientes')}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {isEditing ? t('admin.clienteEdit') : t('admin.clienteNew')}
              </h1>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 px-4 sm:px-0">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Dados do cliente</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData((prev) => ({ ...prev, nome: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                <input
                  type="text"
                  value={formData.telefone}
                  onChange={(e) => setFormData((prev) => ({ ...prev, telefone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data de nascimento</label>
                <input
                  type="date"
                  value={formData.dataNascimento}
                  onChange={(e) => setFormData((prev) => ({ ...prev, dataNascimento: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nacionalidade</label>
                <input
                  type="text"
                  value={formData.nacionalidade}
                  onChange={(e) => setFormData((prev) => ({ ...prev, nacionalidade: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Número do passaporte</label>
                <input
                  type="text"
                  value={formData.passaporteNumero}
                  onChange={(e) => setFormData((prev) => ({ ...prev, passaporteNumero: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Validade do passaporte</label>
                <input
                  type="date"
                  value={formData.passaporteValidade}
                  onChange={(e) => setFormData((prev) => ({ ...prev, passaporteValidade: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Endereço</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Logradouro</label>
                <input
                  type="text"
                  value={formData.endereco.logradouro}
                  onChange={(e) => setFormData((prev) => ({ ...prev, endereco: { ...prev.endereco, logradouro: e.target.value } }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Número</label>
                <input
                  type="text"
                  value={formData.endereco.numero}
                  onChange={(e) => setFormData((prev) => ({ ...prev, endereco: { ...prev.endereco, numero: e.target.value } }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Complemento</label>
                <input
                  type="text"
                  value={formData.endereco.complemento}
                  onChange={(e) => setFormData((prev) => ({ ...prev, endereco: { ...prev.endereco, complemento: e.target.value } }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bairro</label>
                <input
                  type="text"
                  value={formData.endereco.bairro}
                  onChange={(e) => setFormData((prev) => ({ ...prev, endereco: { ...prev.endereco, bairro: e.target.value } }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
                <input
                  type="text"
                  value={formData.endereco.cidade}
                  onChange={(e) => setFormData((prev) => ({ ...prev, endereco: { ...prev.endereco, cidade: e.target.value } }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CEP</label>
                <input
                  type="text"
                  value={formData.endereco.cep}
                  onChange={(e) => setFormData((prev) => ({ ...prev, endereco: { ...prev.endereco, cep: e.target.value } }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">País</label>
                <input
                  type="text"
                  value={formData.endereco.pais}
                  onChange={(e) => setFormData((prev) => ({ ...prev, endereco: { ...prev.endereco, pais: e.target.value } }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Pipeline</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value as ClienteFormData['status'] }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="lead">Lead</option>
                  <option value="negociacao">Em negociação</option>
                  <option value="cliente_ativo">Cliente ativo</option>
                  <option value="inativo">Inativo</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Responsável</label>
                <select
                  value={formData.responsavelId}
                  onChange={(e) => setFormData((prev) => ({ ...prev, responsavelId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Sem responsável</option>
                  {users.map((user) => (
                    <option key={user._id} value={user._id}>{user.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Origem</label>
                <input
                  type="text"
                  value={formData.origem}
                  onChange={(e) => setFormData((prev) => ({ ...prev, origem: e.target.value }))}
                  placeholder="Ex: site, indicação, redes sociais..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                <textarea
                  value={formData.observacoes}
                  onChange={(e) => setFormData((prev) => ({ ...prev, observacoes: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button type="button" onClick={() => navigate('/admin/crm/clientes')} className="btn-secondary">
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={loading} className="btn-primary flex items-center space-x-2">
              <Save className="w-4 h-4" />
              <span>{loading ? t('common.saving') : t('common.save')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClienteForm;
